import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';

/**
 * Custom composable function for managing the sidebar state and layout.
 * The sidebar can be opened or closed, and its layout is adjusted based on the device type.
 * Provides functions to update the top and bottom panels based on the state of the sidebar.
 *
 * @returns An object containing the following properties:
 *   - updateBottomPanel: A function to update the bottom panel layout based on the sidebar state and device type.
 *   - open: A reactive reference to the sidebar state (open or closed).
 *   - sidebar: A reactive reference to the sidebar object.
 *   - isMobile: A computed property indicating whether the device is a mobile device.
 */
export function useSidebar() {
  const sidebar = useLocalStorage('sidebar', { open: true });
  const open = ref(sidebar.value.open);

  const isMobile = ref(window.document.body.clientWidth < 768);

  const updateIsMobile = () => {
    isMobile.value = window.document.body.clientWidth < 768;
  };

  watch(sidebar.value, () => {
    updateBottomPanel();
    if (sidebar.value.open) {
      setTimeout(() => (open.value = true), 250);
    } else {
      open.value = false;
    }
  });

  watch(isMobile, () => {
    sidebar.value.open = true;
    open.value = true;
    updateBottomPanel();
  });

  onMounted(() => {
    window.addEventListener('resize', updateIsMobile);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile);
  });

  /**
   * Updates the bottom panel based on the state of the sidebar and the device type.
   */
  function updateBottomPanel() {
    const bottomPanel = window.document.querySelector(
      '.bottomPanel'
    ) as HTMLElement | null;
    if (bottomPanel) {
      switch (true) {
        case sidebar.value.open && !isMobile.value:
          bottomPanel?.classList.add('left-[220px]');
          bottomPanel?.classList.remove('left-24');
          bottomPanel?.classList.remove('left-0');
          bottomPanel.style.width = 'calc(100% - 220px)';
          break;
        case isMobile.value:
          bottomPanel?.classList.add('left-0');
          bottomPanel?.classList.remove('left-[220px]');
          bottomPanel.style.width = 'calc(100%)';
          break;
        default:
          bottomPanel?.classList.add('left-24');
          bottomPanel?.classList.remove('left-[220px]');
          bottomPanel?.classList.remove('left-0');
          bottomPanel.style.width = 'calc(100% - 96px)';
          break;
      }
    }
  }

  return {
    updateBottomPanel,
    open,
    sidebar,
    isMobile: computed(() => isMobile.value),
  };
}
